import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY } from "../data/site";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/domestic-tours", label: "Domestic" },
  { to: "/international-tours", label: "International" },
  { to: "/mice-events", label: "MICE" },
  { to: "/farmers-study-tours", label: "Farmers'" },
  { to: "/blog", label: "Stories" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const isHome = pathname === "/";
  const light = isHome && !scrolled;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FDFBF7]/90 border-b border-black/5 shadow-[0_4px_30px_rgba(10,34,28,0.06)] py-2.5"
          : light
          ? "backdrop-blur-md bg-gradient-to-b from-[#0A221C]/55 to-transparent py-4"
          : "backdrop-blur-xl bg-[#FDFBF7]/90 border-b border-black/5 py-3"
      }`}
    >
      <nav className="ll-container flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <span className="rounded-2xl bg-white px-3.5 py-2 shadow-[0_6px_20px_rgba(10,34,28,0.12)] ring-1 ring-black/5 transition-all duration-300">
            <img src={COMPANY.logo} alt="Life Line Tours and Travels Pvt Ltd" className={`w-auto object-contain transition-all duration-300 ${scrolled ? "h-11" : "h-14"}`} />
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-6 xl:gap-7">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={`text-sm font-semibold whitespace-nowrap transition-colors hover:text-[#4ef72d] relative ${
                light ? "text-white/95" : "text-[#0A221C]"
              } ${pathname === l.to ? "text-[#4ef72d]" : ""}`}
            >
              {l.label}
              {pathname === l.to && <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-[#4ef72d] rounded-full" />}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-4 xl:gap-5 pl-2">
          <a
            href={`tel:${COMPANY.phones[0].replace(/\s/g, "")}`}
            data-testid="nav-call"
            className={`hidden xl:flex items-center gap-2 text-sm font-semibold whitespace-nowrap ${light ? "text-white" : "text-[#0A221C]"}`}
          >
            <Phone size={16} /> {COMPANY.phones[0]}
          </a>
          <Link to="/contact" data-testid="nav-contact-cta" className="btn-primary !px-5 !py-2.5 text-sm whitespace-nowrap">
            Enquire Now
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className={`lg:hidden p-2 rounded-full ${light ? "text-white bg-white/10" : "text-[#0A221C]"}`}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#FDFBF7] border-t border-black/5 mt-3">
          <div className="ll-container py-6 flex flex-col gap-4">
            {LINKS.concat({ to: "/contact", label: "Contact" }).map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-[#0A221C] text-base font-medium py-1"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                {l.label}
              </Link>
            ))}
            <a href={`tel:${COMPANY.phones[0].replace(/\s/g, "")}`} className="btn-primary mt-2">
              <Phone size={16} /> Call {COMPANY.phones[0]}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
